import { forwardRef, type MouseEvent } from 'react'

import { Tooltip } from '../Tooltip'
import type { ButtonProps } from './types'
import { cx, hasButtonContent, renderButtonIcon } from './utils'

import './Button.scss'

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
    {
        variant = 'primary',
        size = 'medium',
        leftIcon,
        rightIcon,
        skeleton = false,
        isLoading = false,
        disabled = false,
        tooltip,
        tooltipPlacement,
        className,
        children,
        type = 'button',
        onClick,
        ...rest
    },
    ref
) {
    const hasText = hasButtonContent(children)
    const iconOnly = !hasText && Boolean(leftIcon || rightIcon)
    const showSpinner = isLoading && !skeleton
    const isDisabled = disabled || skeleton || isLoading

    const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
        if (isDisabled) {
            event.preventDefault()
            return
        }
        onClick?.(event)
    }

    return (
        <Tooltip content={tooltip} disabled={!tooltip} placement={tooltipPlacement}>
            <button
                ref={ref}
                type={type}
                className={cx(
                    'Button',
                    `Button--${variant}`,
                    `Button--${size}`,
                    iconOnly && 'Button--iconOnly',
                    skeleton && 'Button--skeleton',
                    showSpinner && 'Button--loading',
                    className
                )}
                disabled={isDisabled}
                aria-disabled={isDisabled || undefined}
                aria-busy={skeleton || isLoading || undefined}
                onClick={handleClick}
                {...rest}
            >
                {leftIcon ? (
                    <span className="Button__icon" aria-hidden>
                        {renderButtonIcon(leftIcon)}
                    </span>
                ) : null}
                {hasText ? <span className="Button__text">{children}</span> : null}
                {rightIcon ? (
                    <span className="Button__icon" aria-hidden>
                        {renderButtonIcon(rightIcon)}
                    </span>
                ) : null}
                {showSpinner ? (
                    <span className="Button__spinner" aria-hidden>
                        <span className="Button__spinnerCircle" />
                    </span>
                ) : null}
            </button>
        </Tooltip>
    )
})

Button.displayName = 'Button'

export default Button
